// 価格の吹き出し (MarkerSpec.label) の寸法・配置と間引きの規則。
// 吹き出しの寸法は画面 (lib/leaflet-setup.ts) と PDF (pdf/render-map.ts) がここを参照して同じ見た目に描く。
// 間引きと置く側は MapSpec を組む側 (章ビルダー) が cullOverlappingLabels で一度だけ決めるので、
// 画面と PDF で同じ点に同じ側の吹き出しが付く。
import type { Point } from '@/lib/api.types';
import type { LabelSide, MarkerSpec } from '@/lib/map-spec';
import { project, type XY } from '@/lib/mercator';

/** 施設・ハザードマーカー (アイコン入りの円) の直径 (px)。 */
export const MARKER_SIZE = 28;
/** 吹き出しを落とした価格点に置く小さな丸の直径 (px, 雲色の縁 2px を含む)。 */
export const DOT_SIZE = 10;
/** 調査地点ピンの直径 (22px + 外側 1px の墨線 ×2)。 */
const CENTER_PIN_SIZE = 24;
export const LABEL_FONT_PX = 11;
export const LABEL_FONT_WEIGHT = 600;
export const LABEL_PAD_X = 6;
export const LABEL_PAD_Y = 3;
/** 吹き出し・丸の縁 (雲色) の太さ。施設マーカーの円と同じ 2px。 */
export const LABEL_BORDER = 2;
export const LABEL_RADIUS = 4;
/** 吹き出し本体の高さ = 文字 11px + 上下 3px + 縁 2px×2。 */
export const LABEL_HEIGHT = LABEL_FONT_PX + LABEL_PAD_Y * 2 + LABEL_BORDER * 2;
/** 本体から点へ向かう三角 (しっぽ) の高さ。底辺は高さの 2 倍 (45°) で、先端が点の座標。 */
export const LABEL_TAIL = 5;
/** 吹き出しを置く側の候補。近い点から順に、この順で最初に収まる側を採る。 */
export const LABEL_SIDES: readonly LabelSide[] = ['above', 'below', 'right', 'left'];
/** 間引き判定で吹き出しの箱に足す余白 (px)。幅の概算誤差と描画側の座標丸めを吸収する。 */
const LABEL_CULL_MARGIN = 1;

export type Rect = { x: number; y: number; w: number; h: number };

/**
 * 吹き出し本体の箱 (点を原点にした相対 px)。w は縁込みの本体幅 (画面は CSS、PDF は measureText で実測)。
 * しっぽは本体の点側の辺の中央から点へ伸びる。
 */
export function labelBodyRect(w: number, side: LabelSide): Rect {
  const h = LABEL_HEIGHT;
  switch (side) {
    case 'above':
      return { x: -w / 2, y: -LABEL_TAIL - h, w, h };
    case 'below':
      return { x: -w / 2, y: LABEL_TAIL, w, h };
    case 'right':
      return { x: LABEL_TAIL, y: -h / 2, w, h };
    case 'left':
      return { x: -LABEL_TAIL - w, y: -h / 2, w, h };
  }
}

// フォント計測なしの概算幅 (全角 = 1em, 半角 ≒ 0.6em)。間引き判定にだけ使い、描画はブラウザ/canvas が実測する。
export function estimateLabelWidth(text: string): number {
  let w = 0;
  for (const ch of text) w += (ch.codePointAt(0) ?? 0) > 0xff ? LABEL_FONT_PX : LABEL_FONT_PX * 0.6;
  return Math.ceil(w + LABEL_PAD_X * 2 + LABEL_BORDER * 2);
}

type Box = { x1: number; y1: number; x2: number; y2: number };

function intersects(a: Box, b: Box): boolean {
  return a.x1 < b.x2 && b.x1 < a.x2 && a.y1 < b.y2 && b.y1 < a.y2;
}

function squareBox(c: XY, size: number): Box {
  const r = size / 2;
  return { x1: c.x - r, y1: c.y - r, x2: c.x + r, y2: c.y + r };
}

// 本体の箱 (余白つき) と、しっぽまで含めた箱 (本体と点を覆う)。
function labelBoxes(c: XY, text: string, side: LabelSide): { body: Box; full: Box } {
  const r = labelBodyRect(estimateLabelWidth(text), side);
  const m = LABEL_CULL_MARGIN;
  const body = { x1: c.x + r.x - m, y1: c.y + r.y - m, x2: c.x + r.x + r.w + m, y2: c.y + r.y + r.h + m };
  const full = {
    x1: Math.min(body.x1, c.x - m),
    y1: Math.min(body.y1, c.y - m),
    x2: Math.max(body.x2, c.x + m),
    y2: Math.max(body.y2, c.y + m),
  };
  return { body, full };
}

function dist2(a: XY, b: XY): number {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
}

// Leaflet はマーカー位置を整数 px に丸めて置く (Marker._setPos → round)。判定も同じ丸めで行う。
function roundXY(p: XY): XY {
  return { x: Math.round(p.x), y: Math.round(p.y) };
}

/**
 * マーカーの重ね順 (下→上) のインデックス列。Leaflet は z-index = 整数 px の y なので南の点ほど上に重なり、
 * 同じ y は後の要素が上。PDF (pdf/render-map.ts) もこの順で描いて画面と同じ重なりにする。
 */
export function stackingOrder(points: XY[]): number[] {
  return points
    .map((p, i) => ({ y: Math.round(p.y), i }))
    .sort((a, b) => a.y - b.y || a.i - b.i)
    .map((e) => e.i);
}

/**
 * 吹き出しの置く側を決め、どこにも収まらない吹き出しを落とす (調査地点に近い点を優先)。
 * LABEL_SIDES の順 (上→下→右→左) に試し、他の吹き出し (しっぽ込み) や調査地点ピン (本体のみ。
 * しっぽがピンに触れるのは許す) と重ならない最初の側を採る。落とした点は小さな丸 (色とツールチップは残る)。
 * 上側の本体は南側の丸より下に重なる (z-index は y 順) ので丸に隠されないが、他の側は他の点の丸が
 * 上に来ることがあるので丸も避ける。
 * 固定ズームの地図向け: fitToMarkers で表示ズームが端末幅に依存する地図には使わない (重なり判定が合わない)。
 */
export function cullOverlappingLabels(markers: MarkerSpec[], center: Point, zoom: number): MarkerSpec[] {
  const c = roundXY(project(center, zoom));
  const px = markers.map((m) => roundXY(project(m.point, zoom)));
  const pin = squareBox(c, CENTER_PIN_SIZE);
  const dots = px.map((p) => squareBox(p, DOT_SIZE));
  const order = markers
    .map((_, i) => i)
    .filter((i) => markers[i].label)
    .sort((a, b) => dist2(px[a], c) - dist2(px[b], c) || a - b);
  const side = new Map<number, LabelSide>();
  const placed: Box[] = [];
  for (const i of order) {
    const text = markers[i].label ?? '';
    for (const s of LABEL_SIDES) {
      const { body, full } = labelBoxes(px[i], text, s);
      if (intersects(body, pin) || placed.some((b) => intersects(full, b))) continue;
      if (s !== 'above' && dots.some((d, j) => j !== i && intersects(full, d))) continue;
      side.set(i, s);
      placed.push(full);
      break;
    }
  }
  return markers.map((m, i) => {
    if (!m.label) return m;
    const s = side.get(i);
    return s ? { ...m, labelSide: s } : { ...m, label: undefined, labelSide: undefined };
  });
}
